// RIBBON group — a labeled cluster of controls (R0, 2026-07-20).
//
// Two renderings, decided by RibbonRow's collapse computation:
//   expanded  — controls row + caption (label + ACTIVE dot slot) + a corner
//               "…" when the group has overflow-only controls or a drawer
//               section (the-drawer-is-the-ribbon-unfolded law);
//   collapsed — a labeled "…" chip whose menu holds ALL the group's controls
//               (vertical layout) plus the same drawer footer.
//
// THE DRAWER-LINK CONTRACT (R1 wires the drawer itself): every group declares
// `drawerSection` (string id or null). The menu footer "More options…" calls
// onOpenDrawer(drawerSection) — nothing else; the ribbon never knows what the
// drawer renders. Ribbon group ids and drawer section ids stay 1:1 by law.
//
// The ACTIVE dot: group.dot is a MIRROR KEY (string); truthy state lights the
// dot on the caption / chip — same rule as the accordion sidebar's dot
// (TbAccordion), derived by the mount from its mirrors, purely presentational
// here.
//
// DISABLED (R1): `disabled` renders the expanded controls inside a
// <fieldset disabled> — the native cascade turns off every button/input in
// the group in one move (no per-primitive disabled props to fork), the
// corner "…" included. A collapsed group disables its chip instead. The
// caption dims via CSS; the ACTIVE dot still shows (state must never hide).
//
// R2.5 (flyout redesign): a group whose ONE inline control is a flyout kind
// absorbs its drawer link INTO that flyout's footer ("More options…" lives
// inside the panel — owner: no bare "…" corners crowding the row). The
// corner "…" then renders only for overflow-only controls; groups made of
// plain buttons keep the classic corner when they still declare a
// drawerSection (Row 2 is untouched by this phase's recomposition).
import React from 'react';
import RibbonControl from './RibbonControl';
import OverflowMenu from './OverflowMenu';
import ScopeChip from '../ScopeChip';

const FLYOUT_KINDS = new Set(['sizefield', 'flyoutchips', 'colorflyout', 'flyoutsliders',
  'outlineflyout', 'bevelflyout', 'threedflyout', 'flyoutmenu', 'effectflyout']);

export default function RibbonGroup({
  group,
  collapsed = false,
  state = {},
  handlers = {},
  onOpenDrawer,
  disabled = false,
}) {
  const dotOn = group.dot ? !!state[group.dot] : false;
  // Control-level hideWhen (U2): a control whose mirror key is truthy is
  // FILTERED OUT entirely — the same posture as the group-level showWhen (a
  // control that must not exist for this kind, not merely a disabled one). The
  // Outline face is hidden for shapes (the Border tool owns shape edges — the
  // no-twins law), present for icons/text. The measure layer renders this same
  // component, so a hidden control costs no width either.
  const shown = (group.controls || []).filter((c) => !(c.hideWhen && state[c.hideWhen]));
  const inline = shown.filter((c) => !c.overflowOnly);
  const overflow = shown.filter((c) => c.overflowOnly);
  // The flyout that carries the group's drawer footer (single inline flyout
  // control + a declared drawerSection → the footer moves inside).
  const footerHost = group.drawerSection
    && inline.length === 1 && FLYOUT_KINDS.has(inline[0].kind)
    ? inline[0].id
    : null;
  const hasMenu = overflow.length > 0 || (!!group.drawerSection && !footerHost);

  const renderControl = (c, inMenu) => (c.kind === 'scope'
    ? (
      <ScopeChip
        key={c.id}
        scope={state[c.read ?? 'scope']}
        onSetScope={(k) => handlers?.[c.handler]?.(k)}
      />
    )
    : (
      <RibbonControl
        key={c.id}
        descriptor={c}
        state={state}
        handlers={handlers}
        inMenu={inMenu}
        // Footer routing (R3): a control-level drawerSection targets ITS OWN
        // flyout footer (multi-control sections — Row 2's Style/Depth); the
        // single-flyout group still absorbs the group-level link (R2.5 law).
        drawerSection={!inMenu
          ? (footerHost === c.id ? group.drawerSection : (c.drawerSection ?? null))
          : null}
        onOpenDrawer={onOpenDrawer}
      />
    ));

  const drawerFooter = group.drawerSection ? (
    <button
      type="button"
      className="rbn-menu-footer"
      data-drawer-section={group.drawerSection}
      onClick={() => onOpenDrawer?.(group.drawerSection)}
    >
      More options…
    </button>
  ) : null;

  if (collapsed) {
    return (
      <div
        className="rbn-group rbn-group-collapsed"
        data-group-id={group.id}
        data-collapsed="true"
        data-disabled={disabled ? 'true' : 'false'}
      >
        <OverflowMenu variant="chip" label={group.label} title={group.label} dot={dotOn} disabled={disabled}>
          <div className="rbn-menu-controls">
            {shown.map((c) => renderControl(c, true))}
          </div>
          {drawerFooter}
        </OverflowMenu>
      </div>
    );
  }

  return (
    <div
      className="rbn-group"
      data-group-id={group.id}
      data-collapsed="false"
      data-disabled={disabled ? 'true' : 'false'}
    >
      <fieldset className="rbn-group-controls" disabled={disabled}>
        {inline.map((c) => renderControl(c, false))}
        {hasMenu && (
          <OverflowMenu variant="corner" title={group.label} disabled={disabled}>
            {overflow.length > 0 && (
              <div className="rbn-menu-controls">
                {overflow.map((c) => renderControl(c, true))}
              </div>
            )}
            {drawerFooter}
          </OverflowMenu>
        )}
      </fieldset>
      <div className="rbn-group-caption">
        <span className="rbn-group-label">{group.label}</span>
        {dotOn && <span className="rbn-dot" title="In use" aria-hidden="true" />}
      </div>
    </div>
  );
}
